import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import {
  ScaleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { profileAPI } from '../../services/api'
import BodyMeasurementForm from '../profile/BodyMeasurementForm'
import { useChartTheme } from '../../hooks/useChartTheme'

const TIME_RANGES = [
  { value: 30, labelKey: 'bodyComposition.1month' },
  { value: 90, labelKey: 'bodyComposition.3months' },
  { value: 180, labelKey: 'bodyComposition.6months' },
  { value: 365, labelKey: 'bodyComposition.1year' },
  { value: 9999, labelKey: 'bodyComposition.allTime' },
]

const CHART_METRICS = {
  weight_kg: { color: '#4F46E5', label: 'bodyComposition.weight', unit: 'kg', invertColor: true },
  body_fat_percentage: { color: '#EA580C', label: 'profile.bodyFat', unit: '%', invertColor: true },
  muscle_mass_kg: { color: '#16A34A', label: 'profile.muscleMass', unit: 'kg', invertColor: false },
  bmi: { color: '#7C3AED', label: 'BMI', unit: '', invertColor: true },
  body_water_percentage: { color: '#0891B2', label: 'profile.bodyWater', unit: '%', invertColor: false },
  visceral_fat_level: { color: '#DB2777', label: 'profile.visceralFat', unit: '', invertColor: true },
  skeletal_muscle_percentage: { color: '#059669', label: 'profile.skeletalMuscle', unit: '%', invertColor: false },
  bone_mass_kg: { color: '#6366F1', label: 'profile.boneMass', unit: 'kg', invertColor: false },
  metabolic_age: { color: '#D97706', label: 'profile.metabolicAge', unit: '', invertColor: true },
  protein_percentage: { color: '#10B981', label: 'profile.proteinPercentage', unit: '%', invertColor: false },
  subcutaneous_fat_percentage: { color: '#F59E0B', label: 'profile.subcutaneousFat', unit: '%', invertColor: true },
  fat_free_body_weight_kg: { color: '#8B5CF6', label: 'profile.fatFreeWeight', unit: 'kg', invertColor: false },
  bmr_scale: { color: '#EC4899', label: 'profile.bmr', unit: 'kcal', invertColor: false },
}

const SOURCE_STYLES = {
  manual: { bg: 'bg-gray-100', text: 'text-gray-600' },
  smart_scale: { bg: 'bg-blue-100', text: 'text-blue-700' },
  email_intake: { bg: 'bg-green-100', text: 'text-green-700' },
}

export default function BodyCompositionChart() {
  // VH-133: recharts takes colours as props, so it inherits nothing from
  // the design system — these follow the active theme instead.
  const chart = useChartTheme()
  const { t } = useTranslation()
  const [timeRange, setTimeRange] = useState(90)
  const [enabledMetrics, setEnabledMetrics] = useState(['weight_kg'])
  const [expandedId, setExpandedId] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['measurementHistory', timeRange],
    queryFn: () => profileAPI.getMeasurementHistory({ days: timeRange, limit: 100 }),
    select: (response) => response.data,
  })

  const measurements = historyData?.measurements || []

  // Build chart data — chronological order (oldest first for chart)
  const chartData = [...measurements].reverse().map((m) => {
    const entry = {
      date: new Date(m.measured_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    }
    // Map all direct fields that exist in CHART_METRICS
    for (const key of Object.keys(CHART_METRICS)) {
      if (m[key] !== null && m[key] !== undefined) {
        entry[key] = m[key]
      }
    }
    // Merge additional_metrics (overflow fields from scale)
    if (m.additional_metrics) {
      Object.entries(m.additional_metrics).forEach(([key, value]) => {
        if (CHART_METRICS[key] && value !== null) {
          entry[key] = value
        }
      })
    }
    return entry
  })

  // Check which metrics have any data
  const availableMetrics = Object.keys(CHART_METRICS).filter((key) =>
    chartData.some((d) => d[key] !== null && d[key] !== undefined)
  )

  const toggleMetric = (key) => {
    setEnabledMetrics((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  // Delta comparison — latest vs previous
  const latest = measurements[0]
  const previous = measurements[1]

  const getDelta = (field) => {
    if (!latest || !previous) return null
    const latestVal = latest[field] ?? latest.additional_metrics?.[field]
    const prevVal = previous[field] ?? previous.additional_metrics?.[field]
    if (latestVal == null || prevVal == null) return null
    return +(latestVal - prevVal).toFixed(1)
  }

  const getSourceLabel = (source) => {
    return t(`bodyComposition.sources.${source}`, source)
  }

  // All metrics from a measurement (direct + additional_metrics)
  const getAllMetrics = (m) => {
    const metrics = {}
    if (m.body_fat_percentage) metrics.body_fat_percentage = m.body_fat_percentage
    if (m.muscle_mass_kg) metrics.muscle_mass_kg = m.muscle_mass_kg
    if (m.additional_metrics) {
      Object.entries(m.additional_metrics).forEach(([key, value]) => {
        if (value !== null && value !== undefined) metrics[key] = value
      })
    }
    return metrics
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="card"><div className="card-body animate-pulse"><div className="h-64 bg-gray-200 rounded"></div></div></div>
      </div>
    )
  }

  if (measurements.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-12">
          <ScaleIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">{t('bodyComposition.noData')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('bodyComposition.noDataHint')}</p>
          <button onClick={() => setShowForm(true)} className="mt-4 btn-primary">
            <PlusIcon className="h-4 w-4 mr-1 inline" />
            {t('profile.logMeasurement', 'Log Measurement')}
          </button>
          <BodyMeasurementForm isOpen={showForm} onClose={() => setShowForm(false)} />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Delta Comparison Card */}
      {latest && previous && (
        <div className="card">
          <div className="card-body">
            <h4 className="text-sm font-medium text-gray-500 mb-3">
              {t('bodyComposition.latestVsPrevious')}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {Object.entries(CHART_METRICS).map(([field, config]) => {
                const delta = getDelta(field)
                if (delta === null) return null
                const isPositive = delta > 0
                const isGood = config.invertColor ? delta <= 0 : delta >= 0
                const color = delta === 0 ? 'text-gray-500' : isGood ? 'text-green-600' : 'text-red-600'
                const Icon = delta === 0 ? MinusIcon : isPositive ? ArrowTrendingUpIcon : ArrowTrendingDownIcon
                const label = field === 'bmi' ? 'BMI' : t(config.label, field)

                return (
                  <div key={field} className="text-center">
                    <p className="text-xs text-gray-500">{label}</p>
                    <div className={`flex items-center justify-center gap-1 ${color}`}>
                      <Icon className="h-4 w-4" />
                      <span className="text-lg font-semibold">
                        {delta > 0 ? '+' : ''}{delta} {config.unit}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Trend Chart */}
      <div className="card">
        <div className="card-header flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h3 className="text-lg font-medium text-gray-900">{t('bodyComposition.trendChart')}</h3>
          <div className="flex items-center gap-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value))}
              className="form-select text-sm"
            >
              {TIME_RANGES.map((r) => (
                <option key={r.value} value={r.value}>{t(r.labelKey)}</option>
              ))}
            </select>
            <button onClick={() => setShowForm(true)} className="btn-primary flex items-center">
              <PlusIcon className="h-4 w-4 mr-1" />
              {t('profile.logMeasurement', 'Log')}
            </button>
          </div>
        </div>
        <div className="card-body">
          {/* Metric Toggles */}
          <div className="flex flex-wrap gap-2 mb-4">
            {availableMetrics.map((key) => {
              const active = enabledMetrics.includes(key)
              const config = CHART_METRICS[key]
              return (
                <button
                  key={key}
                  onClick={() => toggleMetric(key)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    active
                      ? 'border-transparent text-white'
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                  style={active ? { backgroundColor: config.color } : {}}
                >
                  {key === 'bmi' ? 'BMI' : t(config.label, key)}
                </button>
              )
            })}
          </div>

          {/* Chart */}
          {chartData.length >= 2 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: chart.axis }} />
                  <YAxis tick={{ fontSize: 12, fill: chart.axis }} domain={['auto', 'auto']} allowDecimals />
                  <Tooltip {...chart.tooltipProps} />
                  {enabledMetrics.filter((k) => availableMetrics.includes(k)).map((key) => (
                    <Line
                      key={key}
                      type="linear"
                      dataKey={key}
                      stroke={CHART_METRICS[key].color}
                      strokeWidth={2}
                      dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                      activeDot={{ r: 6 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              {chartData.length === 1
                ? `${t('bodyComposition.weight')}: ${chartData[0].weight_kg} kg — ${t('bodyComposition.noChange', 'Need 2+ data points for a trend')}`
                : t('bodyComposition.noData')
              }
            </div>
          )}
        </div>
      </div>

      {/* Measurement History List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">{t('bodyComposition.measurementHistory')}</h3>
        </div>
        <div className="card-body">
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {measurements.map((m, idx) => {
              const prevM = measurements[idx + 1]
              const weightChange = prevM ? +(m.weight_kg - prevM.weight_kg).toFixed(1) : null
              const isExpanded = expandedId === m.id
              const allMetrics = getAllMetrics(m)
              const hasDetails = Object.keys(allMetrics).length > 0
              const sourceStyle = SOURCE_STYLES[m.source] || SOURCE_STYLES.manual

              return (
                <div key={m.id} className="border rounded-lg overflow-hidden">
                  <div
                    className="p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                    onClick={() => hasDetails && setExpandedId(isExpanded ? null : m.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(m.measured_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${sourceStyle.bg} ${sourceStyle.text}`}>
                          {getSourceLabel(m.source)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">{m.weight_kg.toFixed(1)} <span className="text-xs font-normal">kg</span></p>
                        {weightChange !== null && weightChange !== 0 && (
                          <p className={`text-xs ${weightChange < 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {weightChange > 0 ? '+' : ''}{weightChange} kg
                          </p>
                        )}
                      </div>
                      {m.body_fat_percentage && (
                        <div className="text-right border-l pl-3">
                          <p className="text-sm font-semibold text-orange-600">{m.body_fat_percentage.toFixed(1)}%</p>
                          <p className="text-xs text-gray-500">{t('profile.bodyFat', 'Body Fat')}</p>
                        </div>
                      )}
                      {hasDetails && (
                        <button className="p-1 text-gray-400">
                          {isExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                  {isExpanded && hasDetails && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-3">
                        {Object.entries(allMetrics).map(([key, value]) => (
                          <div key={key} className="bg-gray-50 px-2 py-1.5 rounded text-sm">
                            <p className="text-xs text-gray-500">{t(`profile.${key}`, key)}</p>
                            <p className="font-medium text-gray-900">
                              {typeof value === 'number' ? value.toFixed(1) : value}
                            </p>
                          </div>
                        ))}
                      </div>
                      {m.notes && (
                        <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">{m.notes}</p>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <BodyMeasurementForm isOpen={showForm} onClose={() => setShowForm(false)} />
    </div>
  )
}
